import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent, FocusEvent } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

export interface RadioOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

interface Props<T> {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName?: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  selectedValue?: T;
  id?: string;
  validators?: Validators<T>;
  onChange?: (value: T) => void;
  propertyName: string;
  required?: boolean;
  disabled?: boolean;
  options: RadioOption<T>[];
}

const HorizontalRadioInput = <T extends string>(props: Props<T>) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, info, tooltip } = useFieldData(props.fieldData, name);
  const modelState = props.modelState;

  const onFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e.target.value as T;
    if (props.onChange) {
      props.onChange(fieldValue);
    }

    if (props.setModelState) {
      props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, fieldValue));
    }
  };

  const onFieldBlur = (_: FocusEvent<HTMLInputElement>) => {
    if (props.setModelState) {
      props.setModelState(
        unobtrusiveValidation(props.validators, modelState, props.propertyName, props.selectedValue as T)
      );
    }
  };

  const id = props.id || fieldName;
  const labelId = `${id}-label`;
  const hasError = hasValidationMessage(modelState, props.propertyName);
  const errorId = `${id}-error`;

  return (
    <div className="form-row">
      <div className="form-item">
        <div className="form-input">
          <div className="form-label">
            <label id={labelId} htmlFor={id}>
              {heading.value}
            </label>
            <Tooltip fieldName={fieldName} value={tooltip.value} />
          </div>
        </div>
        <div
          id={id}
          role="radiogroup"
          aria-labelledby={labelId}
          aria-required={props.required}
          aria-invalid={hasError}
          aria-errormessage={hasError ? errorId : undefined}
          className="form-item radio"
          onBlur={onFieldBlur}
        >
          {(props.options || []).map((radioItem, i) => {
            const radioBtnId = radioItem.value ? radioItem.value : "";
            const radioBtnLabel = radioItem.label ? radioItem.label : "";
            const radioBtnFieldName = `${props.id || fieldName}_${radioBtnId}`;
            const tooltipFieldName = `Tooltip${radioBtnFieldName.replace(/\d+/g, "")}`;
            const radioBtnTooltip = props.fieldData[tooltipFieldName]?.value;

            return (
              <React.Fragment key={i}>
                <input
                  id={radioBtnFieldName}
                  name={fieldName}
                  type="radio"
                  value={radioBtnId}
                  required={props.required}
                  disabled={radioItem.disabled || props.disabled || false}
                  onChange={onFieldChange}
                  checked={props.selectedValue === radioBtnId}
                />
                <label htmlFor={radioBtnFieldName} className="radio">
                  {radioBtnLabel}
                  <Tooltip fieldName={radioBtnFieldName} value={radioBtnTooltip} />
                </label>
              </React.Fragment>
            );
          })}
        </div>
        <div className="form-item">{info.value && <span>{info.value}</span>}</div>
      </div>
      <ValidationMessage modelState={props.modelState} property={props.propertyName} id={errorId} />
    </div>
  );
};

export default HorizontalRadioInput;
